// src/pages/CategoryPage.jsx
import React, { useEffect, useState, useContext } from "react";
import { useParams, useLocation } from "react-router-dom"; 
import "../style/CategoryPage.css";
import InquiryModal from "../components/InquiryModal";
import { ProductsContext } from "../components/ProductsContext"; // ✅ global cache

const CategoryPage = () => {
  const { categoryName } = useParams(); 
  const location = useLocation(); 
  const allProducts = useContext(ProductsContext); // ✅ products from cache

  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [priceRange, setPriceRange] = useState([0, 100000]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [sortOrder, setSortOrder] = useState("default");
  const [inquiryProduct, setInquiryProduct] = useState(null);

  // ✅ New states for mobile drawers
  const [showFilter, setShowFilter] = useState(false);
  const [showSort, setShowSort] = useState(false);

  // 🔹 Extract highlight ID from query string
  const queryParams = new URLSearchParams(location.search);
  const highlightId = queryParams.get("highlight");

  // 🔹 Instead of Firestore snapshot, use global cached products
  useEffect(() => {
    if (allProducts && allProducts.length > 0) {
      setProducts(allProducts);
    }
  }, [allProducts]);

  // 🔹 Filtering Logic
  useEffect(() => {
    let result = [...products];

    // Category filter (comes from route param)
    if (categoryName) {
      result = result.filter((p) => p.category === categoryName);
    }

    // Price filter
    result = result.filter(
      (p) => p.price >= priceRange[0] && p.price <= priceRange[1]
    );

    // Brand filter
    if (selectedBrands.length > 0) {
      result = result.filter((p) => selectedBrands.includes(p.brand));
    }

    // Sorting
    if (sortOrder === "lowToHigh") {
      result.sort((a, b) => a.price - b.price);
    } else if (sortOrder === "highToLow") {
      result.sort((a, b) => b.price - a.price);
    }

    setFilteredProducts(result);
  }, [priceRange, selectedBrands, sortOrder, products, categoryName]);

  // 🔹 Scroll + highlight effect
  useEffect(() => {
    if (highlightId) {
      const el = document.getElementById(`product-${highlightId}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("highlighted");
        setTimeout(() => el.classList.remove("highlighted"), 3000);
      }
    }
  }, [filteredProducts, highlightId]);

  const handleBrandChange = (brand) => {
    setSelectedBrands((prev) =>
      prev.includes(brand) ? prev.filter((b) => b !== brand) : [...prev, brand]
    );
  };

  return (
    <div className="category-page">
      {/* Sidebar Filters (Desktop Only) */}
      <aside className="filters">
        <h4>Filter By Price</h4>
        <input
          type="range"
          min="0"
          max="100000"
          value={priceRange[1]}
          onChange={(e) => setPriceRange([0, Number(e.target.value)])}
        />
        <p>
          Price Range: ₹{priceRange[0]} - ₹{priceRange[1]}
        </p>

        <h4>Filter By Brand</h4>
        {[...new Set(
          products
            .filter((p) => p.category === categoryName) // ✅ only brands in this category
            .map((p) => p.brand)
        )].map((brand) => (
          <div key={brand}>
            <input
              type="checkbox"
              checked={selectedBrands.includes(brand)}
              onChange={() => handleBrandChange(brand)}
            />
            <label>{brand}</label>
          </div>
        ))}
      </aside>

      {/* Mobile Floating Buttons */}
      <div className="mobile-actions">
        <button onClick={() => setShowFilter(true)}>Filter</button>
        <button onClick={() => setShowSort(true)}>Sort</button>
      </div>

      {/* Filter Drawer (Mobile) */}
      {showFilter && (
        <div className="drawer">
          <div className="drawer-content">
            <button className="close-btn" onClick={() => setShowFilter(false)}>✕</button>
            <h4>Filter By Price</h4>
            <input
              type="range"
              min="0"
              max="100000"
              value={priceRange[1]}
              onChange={(e) => setPriceRange([0, Number(e.target.value)])}
            />
            <p>₹{priceRange[0]} - ₹{priceRange[1]}</p>

            <h4>Filter By Brand</h4>
            {[...new Set(
              products
                .filter((p) => p.category === categoryName)
                .map((p) => p.brand)
            )].map((brand) => (
              <div key={brand}>
                <input
                  type="checkbox"
                  checked={selectedBrands.includes(brand)}
                  onChange={() => handleBrandChange(brand)}
                />
                <label>{brand}</label>
              </div>
            ))}

            <button className="apply-btn" onClick={() => setShowFilter(false)}>Apply</button>
          </div>
        </div>
      )}

      {/* Sort Drawer (Mobile) */}
      {showSort && (
        <div className="drawer">
          <div className="drawer-content">
            <button className="close-btn" onClick={() => setShowSort(false)}>✕</button>
            <h4>Sort By</h4>
            <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
              <option value="default">Default</option>
              <option value="lowToHigh">Price: Low to High</option>
              <option value="highToLow">Price: High to Low</option>
            </select>
            <button className="apply-btn" onClick={() => setShowSort(false)}>Apply</button>
          </div>
        </div>
      )}

      {/* Products */}
      <main className="products-section">
        <div className="products-header">
          <h2>{categoryName ? categoryName : "All Products"}</h2>
          <select onChange={(e) => setSortOrder(e.target.value)}>
            <option value="default">Default</option>
            <option value="lowToHigh">Price Low to High</option>
            <option value="highToLow">Price High to Low</option>
          </select>
        </div>

        <div className="products-grid">
          {filteredProducts.length > 0 ? (
            filteredProducts.map((product) => (
              <div
                className="product-card"
                key={product.id}
                id={`product-${product.id}`}
                data-prerender="product" // ✅ needed for highlight
              >
                {product.discount > 0 && (
                  <span className="discount-badge">
                    {product.discount}% OFF
                  </span>
                )}
                <img src={product.img} alt={product.title} />
                <h4>{product.title}</h4>
                <p className="brand">{product.brand}</p>
                <p className="mrp">₹{product.mrp?.toLocaleString()}</p>
                <p className="price">₹{product.price?.toLocaleString()}</p>
                <p
                  className={`stock ${product.stock ? "in" : "out"}`}
                >
                  {product.stock ? "In Stock" : "Out of Stock"}
                </p>
                <button
                  className="buy-btn"
                  onClick={() => setInquiryProduct(product)}
                >
                  Inquire Now
                </button>
              </div>
            ))
          ) : (
            <p>No products found</p>
          )}
        </div>
      </main>

      {/* Inquiry Modal here */}
      <InquiryModal
        isOpen={!!inquiryProduct}
        onClose={() => setInquiryProduct(null)}
        product={inquiryProduct}
      />
    </div>
  );
};

export default CategoryPage;
